/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { PerspectiveCamera, View } from '@react-three/drei';
import { App, Avatar, Button, ConfigProvider, Dropdown, theme as antdTheme } from 'antd';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { useStore } from '../stores/common.ts';
import { clearUndoHistory } from '../undo/setWithUndo.ts';
import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { useIsMobile } from '../hooks.ts';
import { useAuthHandlers } from '../useAuthHandlers.ts';
import * as Selector from '../stores/selector';
import { BUILD_TIME_EST, ProjectType } from '../constants.ts';
import { auth } from '../firebase.ts';
import aimsLogo from '../assets/aims-logo-250.png';
import ifiLogo from '../assets/ifi-logo.png';
import StandaloneLanguageSelect from '../components/standaloneLanguageSelect.tsx';
import LegalAcceptancePrompt from '../legal/legalAcceptancePrompt.tsx';
import { LEGAL_VERSION } from '../legal/legalVersion.ts';
import { readCachedAcceptance, writeCachedAcceptance } from '../legal/legalAcceptanceCache.ts';
import { GlobalOutlined, UserOutlined } from '@ant-design/icons';
import { screenNameOf } from '../screenName.ts';
import {
  C60Scene,
  CrystalScene,
  DnaScene,
  DockingScene,
  PolymerScene,
  ProteinScene,
  SemiconductorScene,
  SceneProps,
  NanotubeScene,
} from './landingScenes.tsx';
import { ProjectUtil } from '../project/ProjectUtil.ts';
import { askToSaveThen } from '../saveReminder.tsx';

const LEGAL_BASE = import.meta.env.BASE_URL || '/';
const TERMS_URL = LEGAL_BASE + 'terms';
const PRIVACY_URL = LEGAL_BASE + 'privacy';

// Exactly 4 columns per row on desktop (rows wrap after the 4th tile); a
// responsive auto-fit on phones collapses to 2×2 / 1 column by width. We use
// a CSS grid (not flex with wrap) so tile aspect ratios stay consistent.
const GRID_TEMPLATE_DESKTOP = 'repeat(4, 1fr)';
const GRID_TEMPLATE_MOBILE = 'repeat(auto-fit, minmax(160px, 1fr))';

interface ProjectTileProps {
  title: string;
  blurb: string;
  // Passed as a component reference (not JSX) so the tile can inject
  // `paused` and `onReady` props — needed for hover-pause and the loading
  // shimmer that hides until the scene has its data.
  SceneComponent: React.ComponentType<SceneProps>;
  onSelect: () => void;
  // Stagger index for the entrance animation; the keyframe is defined once
  // at the page root and each tile picks its own delay.
  animationIndex: number;
  // Text for the "under construction" ribbon; when set, the tile shows the
  // badge (the tile is still selectable — this only signals work-in-progress).
  underConstructionLabel?: string;
}

const ProjectTile = React.memo(
  ({ title, blurb, SceneComponent, onSelect, animationIndex, underConstructionLabel }: ProjectTileProps) => {
    const [hover, setHover] = useState(false);
    const [ready, setReady] = useState(false);
    // Stable callback so the scene's useEffect doesn't re-fire on every render.
    const handleReady = useCallback(() => setReady(true), []);
    return (
      <button
        type="button"
        onClick={onSelect}
        onMouseEnter={(e) => {
          setHover(true);
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
          e.currentTarget.style.borderColor = 'rgba(120, 180, 255, 0.5)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          setHover(false);
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          background: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: '14px',
          cursor: 'pointer',
          color: '#f5f5f5',
          textAlign: 'left',
          font: 'inherit',
          transition: 'transform 0.15s ease, background 0.15s ease, border-color 0.15s ease',
          // Staggered fade-up. `both` keeps the initial (opacity 0) state
          // applied before the animation starts so we don't flash full-opacity
          // tiles for one frame.
          animation: `aims-landing-fade-up 0.45s cubic-bezier(0.2, 0.7, 0.3, 1) ${animationIndex * 0.08}s both`,
        }}
      >
        <div style={{ position: 'relative' }}>
          {/* drei's <View> renders its OWN DOM element here (an `as`-prop div by
            default) and the canvas tracks that internal ref via the View.Port
            tunnel. Don't wrap it in a separate viewport div — the user-passed
            `track` is ignored on the HtmlView path. Style the View directly. */}
          <View
            style={{
              width: '100%',
              aspectRatio: '1 / 1',
              borderRadius: '8px',
              // Subtle inner gradient so the 3D scene reads against the tile
              // background instead of disappearing into it.
              background:
                'radial-gradient(60% 60% at 50% 40%, rgba(80, 140, 220, 0.18) 0%, rgba(0, 0, 0, 0) 70%), rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Each tile gets its own camera + lights so scenes don't bleed
              into each other. drei's <View> creates an isolated scissor
              region. The scene receives paused (drives AutoRotate) and
              onReady (drives the loading shimmer below). */}
            <PerspectiveCamera makeDefault position={[0, 0, 6]} fov={40} />
            <ambientLight intensity={0.55} />
            <directionalLight position={[3, 4, 5]} intensity={0.8} />
            <directionalLight position={[-3, -2, -4]} intensity={0.3} />
            <SceneComponent paused={hover} onReady={handleReady} />
          </View>
          {/* Loading shimmer — fades out once the scene reports ready. The
            docking tile fetches a 167 KB PDB and builds an InstancedMesh of
            ~2000 spheres, so its first paint lands a few hundred ms after
            mount; the shimmer prevents the blank-tile flash. */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '8px',
              pointerEvents: 'none',
              opacity: ready ? 0 : 1,
              transition: 'opacity 0.35s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(110deg, rgba(255, 255, 255, 0) 30%, rgba(120, 180, 255, 0.08) 50%, rgba(255, 255, 255, 0) 70%)',
              backgroundSize: '200% 100%',
              animation: 'aims-landing-shimmer 1.6s ease-in-out infinite',
            }}
          >
            <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.55)', letterSpacing: '0.04em' }}>···</span>
          </div>
          {/* "Under construction" icon — signals a work-in-progress project
            type without disabling the tile (the DNA/RNA Lab is still usable,
            just incomplete). Sits above the scene in the top-left corner; the
            label rides in the tooltip so the badge stays compact. */}
          {underConstructionLabel && (
            <div
              title={underConstructionLabel}
              style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '15px',
                pointerEvents: 'none',
              }}
            >
              <span aria-hidden>🚧</span>
            </div>
          )}
        </div>
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#ffffff' }}>{title}</div>
          <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.62)', lineHeight: 1.4 }}>
            {blurb}
          </div>
        </div>
      </button>
    );
  },
);

interface FlatCardProps {
  title: string;
  blurb: string;
  href: string;
  external?: boolean;
}

const FlatCard = React.memo(({ title, blurb, href, external }: FlatCardProps) => (
  <a
    href={href}
    target={external ? '_blank' : undefined}
    rel={external ? 'noopener noreferrer' : undefined}
    style={{
      display: 'block',
      width: '100%',
      padding: '14px 18px',
      borderRadius: '10px',
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.07)',
      color: '#f5f5f5',
      textDecoration: 'none',
      transition: 'background 0.15s ease, border-color 0.15s ease',
    }}
    onMouseEnter={(e) => {
      (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255, 255, 255, 0.06)';
      (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(120, 180, 255, 0.4)';
    }}
    onMouseLeave={(e) => {
      (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255, 255, 255, 0.03)';
      (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255, 255, 255, 0.07)';
    }}
  >
    <div style={{ fontSize: '14px', fontWeight: 600 }}>{title}</div>
    <div style={{ marginTop: '4px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.62)', lineHeight: 1.4 }}>
      {blurb}
    </div>
  </a>
));

interface LandingPageProps {
  // Allow the host to pre-empt the landing's own dismiss action — needed in
  // case future entry points (e.g. a deep-link guard) want to do extra work
  // alongside clearing the flag.
  onDismiss?: () => void;
}

const LandingPage = React.memo(({ onDismiss }: LandingPageProps) => {
  const language = useStore(Selector.language);
  const setCommonStore = useStore(Selector.set);
  const projectTitle = useStore(Selector.projectTitle);
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const lang = useMemo(() => ({ lng: language }), [language]);
  const isMobile = useIsMobile();

  // drei's <View> needs an event source covering the page so pointer events
  // land on the correct tile. We track the root container.
  const rootRef = useRef<HTMLDivElement>(null);

  // Auth subscription — drives the avatar display. We can't read user state
  // from useStore yet because the main app's CloudManager (which usually
  // populates state.user) only mounts AFTER this landing is dismissed.
  // Firebase persists sign-in across page loads, so onAuthStateChanged
  // surfaces a returning user immediately even before useAuthHandlers's own
  // post-redirect resolution runs.
  const [authedUser, setAuthedUser] = useState<FirebaseUser | null>(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setAuthedUser(u ?? null));
    return () => unsub();
  }, []);

  // The letter shown in the avatar when the account has no photo. An account
  // whose only "name" is the NO_NAME placeholder has none, so screenNameOf
  // (not displayName) decides; empty means fall back to the person icon.
  const avatarInitial = (screenNameOf(authedUser) || authedUser?.email || '').charAt(0).toUpperCase();

  // Sign-in MUST go through useAuthHandlers — it handles popup-vs-redirect
  // fallback, anonymous→Google upgrade, and (critically) writes the user's
  // pendingAcceptance to Firestore once auth completes. Plain
  // signInWithPopup here would skip the Terms/Privacy gate and the user
  // would never get their acceptedTermsVersion recorded.
  //
  // The gate itself (LegalAcceptancePrompt) mirrors mainToolBar.tsx: the
  // requested sign-in action is stashed in `pendingSignIn`; the modal
  // resolves it on accept after writing pendingAcceptance to the primitive
  // store, or drops it on decline. Sessions on a browser that already
  // accepted the current LEGAL_VERSION skip the modal via the local cache.
  const { signIn, signOut: handleSignOut } = useAuthHandlers();
  const [pendingSignIn, setPendingSignIn] = useState<(() => void) | null>(null);

  const requestSignIn = (action: () => void) => {
    if (readCachedAcceptance() === LEGAL_VERSION) {
      usePrimitiveStore.getState().set((state) => {
        state.pendingAcceptance = {
          acceptedTermsAt: new Date().toISOString(),
          acceptedTermsVersion: LEGAL_VERSION,
        };
      });
      action();
      return;
    }
    setPendingSignIn(() => action);
  };

  const onAcceptGate = () => {
    const now = new Date().toISOString();
    usePrimitiveStore.getState().set((state) => {
      state.pendingAcceptance = { acceptedTermsAt: now, acceptedTermsVersion: LEGAL_VERSION };
    });
    writeCachedAcceptance(LEGAL_VERSION);
    const action = pendingSignIn;
    setPendingSignIn(null);
    if (action) action();
  };

  const onDeclineGate = () => {
    setPendingSignIn(null);
  };

  const handleSignIn = () => requestSignIn(() => signIn());

  const dismiss = () => {
    usePrimitiveStore.getState().set((state) => {
      state.showLanding = false;
    });
    onDismiss?.();
  };

  const pickProject = (type: ProjectType) => {
    // Picking the current type leaves the in-progress project untouched so it
    // can be resumed — nothing is discarded, so no save prompt.
    if (useStore.getState().projectState.type === type) {
      dismiss();
      return;
    }
    // A different type starts a brand-new, empty project: we replace the whole
    // projectState (rather than only flipping .type) so the previous cloud
    // project's title — shown on the toolbar — is cleared along with its
    // molecules. That discards the current scene, so warn about unsaved edits
    // first (same gate as the Open/Recent project menu items).
    askToSaveThen(modal, () => {
      setCommonStore((state) => {
        state.projectState = ProjectUtil.createDefaultProjectState(type);
      });
      clearUndoHistory();
      dismiss();
    });
  };

  const tiles: {
    title: string;
    blurb: string;
    SceneComponent: React.ComponentType<SceneProps>;
    onSelect: () => void;
    underConstructionLabel?: string;
  }[] = [
    {
      title: t('landingPage.MolecularModeling', lang),
      blurb: t('landingPage.MolecularModelingBlurb', lang),
      SceneComponent: C60Scene,
      onSelect: () => pickProject(ProjectType.MOLECULAR_MODELING),
    },
    {
      title: t('landingPage.DrugDiscovery', lang),
      blurb: t('landingPage.DrugDiscoveryBlurb', lang),
      SceneComponent: DockingScene,
      onSelect: () => pickProject(ProjectType.DRUG_DISCOVERY),
    },
    {
      title: t('landingPage.CrystalBuilder', lang),
      blurb: t('landingPage.CrystalBuilderBlurb', lang),
      SceneComponent: CrystalScene,
      onSelect: () => pickProject(ProjectType.CRYSTAL_BUILDER),
    },
    {
      title: t('landingPage.PolymerBuilder', lang),
      blurb: t('landingPage.PolymerBuilderBlurb', lang),
      SceneComponent: PolymerScene,
      onSelect: () => pickProject(ProjectType.POLYMER_BUILDER),
    },
    {
      title: t('landingPage.ProteinLab', lang),
      blurb: t('landingPage.ProteinLabBlurb', lang),
      // C-alpha backbone ribbon (Crambin) — distinct from Drug Discovery's
      // surface-plus-ligand render.
      SceneComponent: ProteinScene,
      onSelect: () => pickProject(ProjectType.PROTEIN_LAB),
      underConstructionLabel: t('landingPage.UnderConstruction', lang),
    },
    {
      title: t('landingPage.DnaRnaLab', lang),
      blurb: t('landingPage.DnaRnaLabBlurb', lang),
      // Procedural B-form double helix.
      SceneComponent: DnaScene,
      onSelect: () => pickProject(ProjectType.DNA_RNA_LAB),
      underConstructionLabel: t('landingPage.UnderConstruction', lang),
    },
    {
      title: t('landingPage.SemiconductorDesign', lang),
      blurb: t('landingPage.SemiconductorDesignBlurb', lang),
      SceneComponent: SemiconductorScene,
      onSelect: () => pickProject(ProjectType.SEMICONDUCTOR_DESIGN),
      underConstructionLabel: t('landingPage.UnderConstruction', lang),
    },
    {
      title: t('landingPage.NanomaterialDesign', lang),
      blurb: t('landingPage.NanomaterialDesignBlurb', lang),
      // A (6,6) carbon nanotube — the signature finite structure of the type.
      SceneComponent: NanotubeScene,
      onSelect: () => pickProject(ProjectType.NANOMATERIAL_DESIGN),
    },
  ];

  return (
    <div
      ref={rootRef}
      style={{
        position: 'fixed',
        inset: 0,
        // No z-index: the landing is the only thing rendered (appCreator
        // returns early when showLanding is true), and a stacking context
        // here was burying antd popups (Select dropdown defaults to 1050)
        // below the surface.
        // Same layered radial palette LandscapeGate uses, so the landing
        // and the rotate-your-phone gate feel like the same surface.
        background:
          'radial-gradient(120% 80% at 50% -10%, rgba(64, 156, 255, 0.18) 0%, rgba(0, 0, 0, 0) 60%),' +
          'radial-gradient(80% 60% at 50% 110%, rgba(64, 156, 255, 0.10) 0%, rgba(0, 0, 0, 0) 60%),' +
          '#05070d',
        color: '#f5f5f5',
        overflowY: 'auto',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Keyframes for the entrance fade-up and the loading shimmer. Inlined
          via <style> so the landing is self-contained and we don't need a
          separate CSS file. */}
      <style>{`
        @keyframes aims-landing-fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes aims-landing-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* Header — tightens on phones: smaller padding + logo, tagline hidden
          so the language picker and Skip button don't get pushed off-screen. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '14px 16px' : '20px 32px',
          gap: '8px',
          animation: 'aims-landing-fade-up 0.4s ease-out 0s both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', minWidth: 0 }}>
          <img
            src={aimsLogo}
            alt="AIMS"
            width={isMobile ? 28 : 36}
            height={isMobile ? 28 : 36}
            style={{ borderRadius: '6px', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: 700, letterSpacing: '0.02em' }}>
              {t('name.AIMS', lang)}
            </div>
            {!isMobile && (
              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.55)' }}>
                {t('landingPage.Tagline', lang)}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Language picker writes back to the same store the main app reads,
              so a choice made on the landing carries into the workspace. The
              borderless Select shows white text against the dark surface with
              a custom popup theme set below. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'rgba(255, 255, 255, 0.7)' }}>
            {!isMobile && <GlobalOutlined />}
            {/* Borderless Select inherits the parent theme's colorText. The
                standalone pages render it on a white surface; here we're on
                a dark surface, so we localize the dark algorithm to just this
                widget rather than re-styling the whole tree. */}
            <ConfigProvider theme={{ algorithm: antdTheme.darkAlgorithm }}>
              <StandaloneLanguageSelect size="small" />
            </ConfigProvider>
          </span>
          {/* Auth: avatar dropdown when signed in, Sign-in button otherwise.
              Firebase persists across page loads, so signing in here carries
              into the app workspace on dismiss. */}
          {authedUser ? (
            <ConfigProvider theme={{ algorithm: antdTheme.darkAlgorithm }}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'name',
                      label: (
                        <div style={{ fontWeight: 500 }}>
                          {authedUser.displayName || authedUser.email || t('banner.SignedIn', lang)}
                        </div>
                      ),
                      disabled: true,
                    },
                    { type: 'divider' },
                    {
                      key: 'signout',
                      label: t('banner.SignOut', lang),
                      // The landing can sit over a dirty project (Help ->
                      // Welcome re-shows it without dropping projectState), so
                      // warn before signing out drops the ability to save.
                      onClick: () => askToSaveThen(modal, handleSignOut),
                    },
                  ],
                }}
              >
                <a
                  onClick={(e) => e.preventDefault()}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                  title={t('banner.Account', lang)}
                >
                  {/* No screen name and no email (a fresh anonymous account)
                      means there is no initial to show — the generic person
                      icon, not a "?". */}
                  <Avatar
                    size={28}
                    src={authedUser.photoURL ?? undefined}
                    icon={avatarInitial ? undefined : <UserOutlined />}
                    style={{ background: '#0969da' }}
                  >
                    {avatarInitial || undefined}
                  </Avatar>
                </a>
              </Dropdown>
            </ConfigProvider>
          ) : (
            <ConfigProvider theme={{ algorithm: antdTheme.darkAlgorithm }}>
              {/* size="middle" (~32 px) matches the height of the native
                  Continue/Skip buttons next to it (border + 6 px padding +
                  13 px font = ~32 px). antd "small" is 24 px and looked
                  noticeably stunted alongside them. */}
              <Button type="primary" onClick={handleSignIn}>
                {t('banner.SignIn', lang)}
              </Button>
            </ConfigProvider>
          )}
          {/* Resume — compact in-header button. Only rendered when there's a
              project to return to. Tinted to signal "primary positive action"
              against the neutral Skip button on its right. The project title
              is in the tooltip rather than the button label to keep header
              space tight; truncated on mobile to a single chip. */}
          {projectTitle && !isMobile && (
            <button
              type="button"
              onClick={dismiss}
              title={`${t('landingPage.ResumeAction', lang)}: ${projectTitle}`}
              style={{
                background: 'rgba(64, 156, 255, 0.18)',
                border: '1px solid rgba(120, 180, 255, 0.45)',
                color: '#dceaff',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '13px',
                font: 'inherit',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(64, 156, 255, 0.28)';
                e.currentTarget.style.borderColor = 'rgba(120, 180, 255, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(64, 156, 255, 0.18)';
                e.currentTarget.style.borderColor = 'rgba(120, 180, 255, 0.45)';
              }}
            >
              {t('landingPage.ResumeAction', lang)}
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '6px',
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
            title={t('landingPage.SkipHint', lang)}
          >
            {t('landingPage.Skip', lang)}
          </button>
        </div>
      </div>

      {/* Headline */}
      <div
        style={{
          textAlign: 'center',
          padding: isMobile ? '8px 16px 16px' : '12px 24px 24px',
          animation: 'aims-landing-fade-up 0.45s ease-out 0.05s both',
        }}
      >
        <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 600, letterSpacing: '-0.01em' }}>
          {t('landingPage.Headline', lang)}
        </div>
        <div
          style={{
            marginTop: '8px',
            fontSize: isMobile ? '13px' : '14px',
            color: 'rgba(255, 255, 255, 0.6)',
            maxWidth: '640px',
            margin: '8px auto 0',
          }}
        >
          {t('landingPage.Subheadline', lang)}
        </div>
      </div>

      {/* Project tile grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? GRID_TEMPLATE_MOBILE : GRID_TEMPLATE_DESKTOP,
          gap: isMobile ? '12px' : '18px',
          padding: isMobile ? '0 16px 24px' : '0 32px 32px',
          maxWidth: 'min(1600px, 100%)',
          margin: '0 auto',
        }}
      >
        {tiles.map((tile, i) => (
          <ProjectTile
            key={i}
            title={tile.title}
            blurb={tile.blurb}
            SceneComponent={tile.SceneComponent}
            onSelect={tile.onSelect}
            underConstructionLabel={tile.underConstructionLabel}
            // Tile stagger starts after the header+headline+resume animations
            // so the grid feels like the "main act."
            animationIndex={i + 2}
          />
        ))}
      </div>

      {/* Secondary row: Commons + Tutorial + About — flat 2D, navigate to
          standalone routes registered in myApp.tsx. auto-fit reflows to 1
          column on phones (where 3 columns would squish blurbs to slivers)
          while still filling the project-grid width on desktop. */}
      <div
        style={{
          padding: isMobile ? '4px 16px 24px' : '8px 32px 32px',
          maxWidth: 'min(1600px, 100%)',
          margin: '0 auto',
          animation: 'aims-landing-fade-up 0.45s ease-out 0.55s both',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'rgba(255, 255, 255, 0.45)',
            marginBottom: '12px',
          }}
        >
          {t('landingPage.MoreSectionHeader', lang)}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: isMobile ? '12px' : '18px',
          }}
        >
          <FlatCard
            title={t('landingPage.Commons', lang)}
            blurb={t('landingPage.CommonsBlurb', lang)}
            href={LEGAL_BASE + 'commons'}
          />
          <FlatCard
            title={t('landingPage.LessonCommons', lang)}
            blurb={t('landingPage.LessonCommonsBlurb', lang)}
            href={LEGAL_BASE + 'lesson-commons'}
          />
          <FlatCard
            title={t('landingPage.Tutorial', lang)}
            blurb={t('landingPage.TutorialBlurb', lang)}
            href={LEGAL_BASE + 'tutorial'}
          />
          <FlatCard
            title={t('landingPage.About', lang)}
            blurb={t('landingPage.AboutBlurb', lang)}
            href="https://intofuture.org/aims.html"
            external
          />
        </div>
      </div>

      {/* Footer — same copyright + Terms/Privacy links as the main app, so
          the landing satisfies the "is this a real product" sniff test and
          gives the legal pages a discoverable entry point even before the
          user picks a project. IFI mark sits inline with the copyright line
          as a standalone glyph (no text — the institute is named on the next
          line anyway). */}
      <div
        style={{
          padding: isMobile ? '8px 16px 24px' : '12px 32px 32px',
          textAlign: 'center',
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.45)',
          userSelect: 'none',
          animation: 'aims-landing-fade-up 0.45s ease-out 0.65s both',
        }}
      >
        <img
          src={ifiLogo}
          alt="Institute for Future Intelligence"
          height={16}
          style={{ verticalAlign: 'middle', marginRight: '8px', opacity: 0.75 }}
        />
        &copy;{new Date().getFullYear()} {t('name.IFI', lang)}. {t('word.Built', lang) + ' ' + BUILD_TIME_EST + '. '}
        <a
          href={TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('aboutUs.TermsShort', lang)}
        </a>
        <span> · </span>
        <a
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('aboutUs.PrivacyShort', lang)}
        </a>
      </div>

      {/* Legal acceptance gate — same modal mainToolBar uses. Mounts when
          pendingSignIn is set (the user clicked Sign in and we haven't
          shortcut via the local cache). Accept writes the acceptance to
          the primitive store + browser cache, then runs the stashed
          sign-in. Decline drops the sign-in attempt. */}
      {pendingSignIn && <LegalAcceptancePrompt onAccept={onAcceptGate} onDecline={onDeclineGate} />}

      {/* Shared canvas. Stays fixed behind the DOM tiles; drei's <View>
          components inside each tile draw into scissor regions tracking
          their viewportRef. pointer-events: none so the DOM buttons get
          the clicks (the scenes are decorative, not interactive). */}
      <Canvas
        eventSource={rootRef as unknown as React.MutableRefObject<HTMLElement>}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <View.Port />
      </Canvas>
    </div>
  );
});

export default LandingPage;
